// MACHINERY V2 (Stroje) — "what is this machine doing", DECIDED ONCE
// (M2.16b fix pass, review finding F6/F7/F11).
//
// ============================================================
//  WHY THIS FILE EXISTS — AN INVARIANT FOUR COPIES COULD NOT HOLD
// ============================================================
// Five surfaces render the same sentence about the same machine: the V1 compact
// card's footer, V3's phone row, V4's compact status row, V3's grouped row and
// the V3 table's AKTUÁLNE cell. Four of them were written in M2.16b, each
// re-deriving the branch from the same presentation flags, and their headers all
// assert some form of "a phone row and a table row can never describe the same
// machine differently".
//
// They already had. One added a no-agent branch, one fell back to `S/N …`, one
// fell back to the no-telemetry explainer, and one had no branch at all — so an
// unconnected machine read four different ways on four surfaces of the same
// fleet. Hand-kept copies cannot hold that invariant; a shared derivation can.
//
// >>> THE PRECEDENCE LIVES HERE AND NOWHERE ELSE. <<<
//   1. alarm        — an active (or last-known) fault outranks everything.
//   2. program      — only while running, and only with a program on the wire.
//   3. noTelemetry  — no MachineConnection row at all.
//   4. freshness    — the heartbeat/since stamp, when the wire carries one.
//   5. none         — the wire carries nothing to say. Say nothing.
// This is the SAME order all five surfaces already intended; it is now written
// down once. Adding a branch means adding a member to the union below, and every
// consumer's `switch` stops compiling until it handles it — which is the point.
//
// IT READS PRESENTATION FLAGS, NEVER RAW TELEMETRY VERDICTS. `p.showAlarmStrip`
// already folds in `alarms.length > 0` (cardStates.ts:277, re-read while writing
// this file), so the `p.showAlarmStrip && t.alarms.length > 0` re-test the four
// M2.16b files each carried — in files whose own headers forbid it — is gone
// rather than centralised. `t.alarms[0]` below is safe FOR THAT REASON, not by
// luck.
//
// WHAT IS DELIBERATELY *NOT* HERE: the wording and the typography. A wall row
// says "bez agenta" where a card prints the full explainer and the table prints
// a dash; the compact card draws the alarm as a tinted callout box where the
// phone row draws it as one red mono line. Those are differences of REGISTER on
// the same branch, and they stay expressible — through fleetActivityLine's
// options for the one-line surfaces, and through the consumer's own `switch` for
// the two that draw panels. What may never differ again is WHICH branch a
// machine falls into.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import {
  MachineryV2Alarm,
  MachineryV2CardPresentation,
  MachineryV2CardState,
  MachineryV2Telemetry,
} from "./cardStates";
import { FLEET_STATE_FILTERS } from "./fleetStatusFilters";

/** The one thing a fleet surface has to say about a machine right now. */
export type FleetActivity =
  | {
      kind: "alarm";
      alarm: MachineryV2Alarm;
      /** Prefix the code as last-known — `stale-alarm` + `offline`. This is
       *  `presentation.alarmLastKnown`, NOT `staleLook` (false for offline by
       *  design); see cardStates' note on the pair. */
      lastKnown: boolean;
    }
  | { kind: "program"; program: string; elapsed: string | null }
  | { kind: "noTelemetry" }
  | { kind: "freshness"; text: string }
  | { kind: "none" };

/**
 * THE derivation. Every surface that describes a machine's current activity
 * calls this and renders the result — none of them re-tests `alarms.length`,
 * `status`, `conn` or `program` for itself.
 */
export const deriveFleetActivity = (
  p: MachineryV2CardPresentation,
  t: MachineryV2Telemetry
): FleetActivity => {
  // showAlarmStrip === (isAlarm || offline) && alarms.length > 0, so the index
  // below cannot be out of range. Trust the flag — do not re-test the length.
  if (p.showAlarmStrip) {
    return { kind: "alarm", alarm: t.alarms[0], lastKnown: p.alarmLastKnown };
  }
  if (p.showProgramLine && t.program) {
    return { kind: "program", program: t.program, elapsed: t.elapsed };
  }
  if (p.showNoTelemetry) return { kind: "noTelemetry" };
  const freshness = t.heartbeat || t.since;
  return freshness ? { kind: "freshness", text: freshness } : { kind: "none" };
};

/** Per-surface register for the ONE-LINE consumers. Each option names a
 *  difference the design package actually draws — not a place to re-open the
 *  precedence above. */
export interface FleetActivityLineOptions {
  /**
   * Print the alarm CODE before its message.
   *
   * TRUE on V3's phone row, whose sub-line is the only place the code appears.
   * FALSE on V4's compact row, where the code is already the big right-hand
   * value beside it and repeating it would print it twice on one line.
   */
  alarmCode: boolean;
  /**
   * What THIS surface says when there is no connection row at all — the wall's
   * terse "bez agenta" versus the card's explainer. `null` means the surface
   * says nothing for that branch and falls through to `fallback`, which is what
   * V3's phone row wants: at 390px the explainer is wider than the machine name
   * above it, and the serial is the more useful line.
   */
  noTelemetry: string | null;
  /** Printed only when nothing else resolved. `S/N …` on V3's phone row, and
   *  nothing at all on the wall. Never a fabricated sentence. */
  fallback?: string | null;
}

/** One line of text for the compact surfaces. The two surfaces that draw a
 *  PANEL (the V1 card footer, the V3 table's AKTUÁLNE cell) switch on
 *  `FleetActivity.kind` themselves instead — same derivation, richer output. */
export const fleetActivityLine = (
  a: FleetActivity,
  o: FleetActivityLineOptions
): string => {
  switch (a.kind) {
    case "alarm": {
      const parts = o.alarmCode
        ? [a.alarm.code, a.alarm.message]
        : [a.alarm.message];
      return parts.filter(Boolean).join(" · ");
    }
    case "program":
      return "▶ " + a.program;
    case "freshness":
      return a.text;
    case "noTelemetry":
      return o.noTelemetry || o.fallback || "";
    default:
      return o.fallback || "";
  }
};

/**
 * >>> DOES A VISIBLE STATUS CHIP GET PAINTED? ONE PREDICATE, ALL SURFACES. <<<
 *
 * `showStatusRow` ALONE DOES NOT PROMISE ONE. A `stale` machine has it TRUE and
 * carries no status — "TelemetryStale" maps to `{ conn: "stale", status: null }`
 * (fleetTelemetry.ts:100, re-read while writing this file) and
 * deriveCardPresentation passes `t.status` straight through for that state
 * (cardStates.ts:268-269) — so StatusBadge hits its `if (!status) return null`
 * (StatusBadge.tsx:72) and paints nothing.
 *
 * The V3 table cell (fleetTableCells' StatusCell), the V3 phone row and the STAV
 * sort key each spelled this predicate out separately, and V2's compact row
 * tested `showStatusRow` alone — so the SAME stale machine on the SAME phone
 * showed a mono "—" in V3 and a bare 7px dot with no chip in V2. Two
 * descriptions, one machine. It is one call now.
 */
export const showsStatusChip = (p: MachineryV2CardPresentation): boolean =>
  p.showStatusRow && (p.showAwaitingBadge || p.badgeStatus !== null);

/** state -> the label key the filter rail, the strip and the mobile sheet's STAV
 *  section already offer, so the "—" tooltip can never name a state differently
 *  from the control that filters for it. FLEET_STATE_FILTERS covers all nine. */
const STATE_LABEL_KEY = FLEET_STATE_FILTERS.reduce<Record<string, string>>(
  (acc, f) => {
    acc[f.state] = f.labelKey;
    return acc;
  },
  {}
);

/** The canonical i18n key for a card state. Used for the "no chip to paint"
 *  mark's tooltip: that mark covers BOTH `unconnected` and a status-less
 *  `stale`, so hardcoding the unconnected label there — as the M2.16b rows did
 *  — mislabels every stale machine. */
export const cardStateLabelKey = (state: MachineryV2CardState): string =>
  STATE_LABEL_KEY[state] || "myMachinery:v2.states.unconnected";
